import { useEffect } from 'react';
import { useAlert } from 'react-alert';

import { UpNextConfigurableStep } from '../../components/up-next-configurable-step';
import { getEligibility } from '../machine/utils';
import { StudentOnboardingMachineContext } from '../types';

type BookingSecondInterstitialPageProps = {
  context: StudentOnboardingMachineContext;
  isPartnerInsurance?: boolean;
  onNext: () => void;
  onExit?: () => void;  // renamed from onSecondary
};

export const BookingSecondInterstitialPage: React.VFC<BookingSecondInterstitialPageProps> = ({
  context,
  isPartnerInsurance,
  onNext,
  onExit  // renamed from onSecondary
}) => {
  const alert = useAlert();
  const {
    psychFirst,
    isPlanLite,
    hasInsurance,
    freePsychiatrySessions,
    freePsychiatryConsultation,
    paidPsychiatryConsultation,
    paidTherapySession,
    canSkipSecondBooking,
  } = getEligibility(context);

  let res = null;

  switch (true) {
    // Free psychiatry sessions with Sponsorship and Insurance
    case freePsychiatrySessions && !psychFirst:
      res = (
        <UpNextConfigurableStep
          onNext={onNext}
          headerTitle="You qualify for"
          headerHighlight="free psychiatry sessions"
          contentTitle="Session costs are fully paid for through your school's partnership with UpLift."
        />
      );
      break;

      // Free psychiatry consultation with Sponsorship and No Insurance
    case freePsychiatryConsultation && !psychFirst:
      res = (
        <UpNextConfigurableStep
          onNext={onNext}
          headerTitle="Your first"
          headerHighlight="psychiatry consultation is free"
          contentTitle="Your consultation is fully paid for through your school's partnership with UpLift."
          contentDescription={(
            <>
              During your initial consultation, your provider will ask about your mental health history and current challenges to help you explore treatment options.
              <br />
              <br />
              If you continue care after the consultation, follow-up sessions are $175.
            </>
          )}
        />
      );
      break;

      // Paid psychiatry consultation with different scenarios
    case paidPsychiatryConsultation:
      if (isPlanLite) {
        // Lite Plan or No Sponsorship: Show standard consultation info
        res = (
          <UpNextConfigurableStep
            onNext={onNext}
            headerTitle="Book your"
            headerHighlight="psychiatry consultation"
            contentTitle="What you can expect"
            contentDescription="During your initial consultation, your provider will ask about your mental health history and current challenges to help you explore treatment options."
          />
        );
      } else if (!hasInsurance) {
        // No Sponsorship & No insurance: Show out-of-pocket pricing
        res = (
          <UpNextConfigurableStep
            onNext={onNext}
            headerTitle="Book your"
            headerHighlight="psychiatry consultation"
            contentTitle="Your school’s partnership doesn’t include psychiatry. But you still have the option to get care through UpLift."
            contentDescription="During your initial consultation, your provider will ask about your mental health history and current challenges to help you explore treatment options. The consultation will cost $250, and follow-up sessions are $175."
            nextButtonText="Schedule psychiatry"
            secondaryButtonText={canSkipSecondBooking && 'Maybe later'}
            onSecondaryClick={onExit}
          />
        );
      } else if (!isPartnerInsurance) {
        // No Sponsorship & Non-partner Insurance: Show non-coverage info
        res = (
          <UpNextConfigurableStep
            onNext={onNext}
            headerTitle="Book your"
            headerHighlight="psychiatry consultation"
            contentTitle="Your school’s partnership doesn’t include psychiatry. But you still have the option to get care through UpLift."
            contentDescription="We do not accept your insurance for psychiatry services. A consultation will cost $250, and follow-up sessions $175." // TODO: Insert non-partner plan name instead of 'your insurance'
            nextButtonText="Schedule psychiatry"
            secondaryButtonText={canSkipSecondBooking && 'Maybe later'}
            onSecondaryClick={onExit}
          />
        );
      } else {
        //  No Sponsorship & Partner Insurance: Show insurance coverage info
        res = (
          <UpNextConfigurableStep
            onNext={onNext}
            headerTitle="Book your"
            headerHighlight="psychiatry consultation"
            contentTitle="Your school’s partnership doesn’t include psychiatry. But UpLift accepts your insurance."
            contentDescription="The amount you pay will depend on your insurance plan and benefits. We’ll provide a cost estimate prior to your session, so you’re always informed."
            nextButtonText="Schedule psychiatry"
            secondaryButtonText={canSkipSecondBooking && 'Maybe later'}
            onSecondaryClick={onExit}
          />
        );
      }
      break;

      // Paid therapy sessions with different pricing scenarios
    case paidTherapySession:
      if (!hasInsurance) {
        // No Sponsorship & No Insurance: Show out-of-pocket therapy pricing
        res = (
          <UpNextConfigurableStep
            onNext={onNext}
            headerTitle="Book your"
            headerHighlight="therapy session"
            contentTitle="Your school’s partnership doesn’t include therapy. But you still have the option to get care through UpLift."
            contentDescription="An initial session costs $145, and follow-up sessions are $125."
            nextButtonText="Schedule therapy"
            secondaryButtonText={canSkipSecondBooking && 'Maybe later'}
            onSecondaryClick={onExit}
          />
        );
      } else if (!isPartnerInsurance) {
        // No Sponsorship & Non-partner Insurance: Show non-coverage therapy info
        res = (
          <UpNextConfigurableStep
            onNext={onNext}
            headerTitle="Book your"
            headerHighlight="therapy session"
            contentTitle="Your school’s partnership doesn’t include therapy. But you still have the option to get care through UpLift."
            contentDescription="We do not accept your insurance for therapy services. An initial session costs $145, and follow-up sessions are $125." // TODO: [INSERT NON-PARTNER PLAN] instead of 'your insurance'
            nextButtonText="Schedule therapy"
            secondaryButtonText={canSkipSecondBooking && 'Maybe later'}
            onSecondaryClick={onExit}
          />
        );
      } else {
        // No Sponsorship & Partner Insurance: Show therapy coverage info
        res = (
          <UpNextConfigurableStep
            onNext={onNext}
            headerTitle="Book your"
            headerHighlight="therapy session"
            contentTitle="Your school’s partnership doesn’t include therapy. But UpLift accepts your insurance."
            contentDescription="The amount you pay will depend on your insurance plan and benefits. We’ll provide a cost estimate prior to your session, so you’re always informed."
            nextButtonText="Schedule therapy"
            secondaryButtonText={canSkipSecondBooking && 'Maybe later'}
            onSecondaryClick={onExit}
          />
        );
      }
      break;
  }

  if (!res) {
    console.log('No matching condition found for BookingSecondInterstitialPage', getEligibility(context));
    onNext();
  }

  useEffect(() => {
    alert.removeAll();
    if (res) {
      if (psychFirst) {
        alert.success(<strong>Psychiatry consultation confirmed</strong>);
      } else {
        alert.success(<strong>Therapy session confirmed</strong>);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return res;
};
